import React, { useEffect, useState, useContext } from 'react';
import { useMutation } from '@apollo/react-hooks';

// selectors
import { useConfigSelectors } from 'src/app/reducers/configReducer';
import { useLanguageSelectors } from 'src/app/reducers/languageReducer';
import { useDataSelectors } from 'src/@infringements/reducers/dataReducer';
import { InfringementDispatchContext } from 'src/@infringements/providers/InfringementsStateProvider';
// actions
import { selectRow } from 'src/@infringements/actions/infringementsActions';
import { dataActionTypes } from 'src/@infringements/newActionTypes';
// components
import { Table } from '@ems/client-design-system';
// function
import { capitalizeObjectKeys } from 'src/utils/objectModifiers';
import { formatATCHeaders, formatATCInfringementData } from 'src/@infringements/functions';

import { ADD_OPERATION_TAGS, REMOVE_OPERATION_TAGS } from 'src/@infringements/mutations';
import { IInfringementsData } from 'src/@infringements/interfaces';
import { hideInfringementColumns } from 'src/utils/tableColumns';
//import { isAtcView } from 'src/utils';
import { useScreenLoadStateContext } from 'src/app/providers/ScreenLoadStateContext';

import { useGetInfringements } from 'src/@infringements/hooks/useGetInfringements';
import { LoadMoreBar } from 'src/componentsModern';
import { useInfringementDataTable } from 'src/@infringements/reducers';
import {
  OrderByType,
  GetInfringementCandidatesQuery,
  GetInfringementsQuery,
} from 'src/app/gql/__generated__/hooks';
import { InfringementTableDataActionType } from 'src/@infringements/reducers/tableData';
//import { InfringementTableDataActionType } from 'src/@infringements/reducers/tableData';

export const TableContainer = ({ source = '' }) => {
  const dispatcher = useContext<any>(InfringementDispatchContext);
  const dataSelectors = useDataSelectors();
  //const atcView = isAtcView(source);
  const {
    data: latestData,
    pageInfo,
    isLoading,
    selectedData,
    areAllRowsSelected,
    isLoadingMore,
  } = dataSelectors.getDataInformation();
  const [addInfringementVectored] = useMutation(ADD_OPERATION_TAGS, {
    update(cache, { data: { addOperationTags } }) {
      dispatcher({
        type: dataActionTypes.INLINE_EDIT_VECTORED,
        data: {
          id: addOperationTags[0].id,
          vectored: true,
        },
      });
    },
  });
  const [removeInfringementVectored] = useMutation(REMOVE_OPERATION_TAGS, {
    update(cache, { data: { removeOperationTags } }) {
      dispatcher({
        type: dataActionTypes.INLINE_EDIT_VECTORED,
        data: {
          id: removeOperationTags[0].id,
          vectored: false,
        },
      });
    },
  });

  const {
    data,
    loading: tableLoading,
    fetchMore,
    loadingMore: tableLoadingMore,
  } = useGetInfringements(dispatcher);
  const { state, dispatch: infringementFilterDispatch } = useInfringementDataTable();

  const handleGetMore = (): void => {
    if (!data || !data?.pageInfo?.hasNextPage) {
      console.log('No more data to load');
      return;
    }

    void fetchMore({
      variables: {
        after: data.pageInfo.endCursor,
        first: loadMoreSize,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        if (state.hasCandidatesEnabled) {
          const typedPrev = prev as GetInfringementCandidatesQuery;
          const typedFetchMoreResult = fetchMoreResult as GetInfringementCandidatesQuery;

          return {
            ...typedPrev,
            infringementCandidateSummariesByTimeRange: {
              ...typedPrev.infringementCandidateSummariesByTimeRange,
              items: [
                ...(typedPrev.infringementCandidateSummariesByTimeRange?.items || []),
                ...(typedFetchMoreResult.infringementCandidateSummariesByTimeRange?.items || []),
              ],
              pageInfo:
                typedFetchMoreResult.infringementCandidateSummariesByTimeRange?.pageInfo ||
                typedPrev.infringementCandidateSummariesByTimeRange?.pageInfo,
              totalCount:
                typedFetchMoreResult.infringementCandidateSummariesByTimeRange?.totalCount ||
                typedPrev.infringementCandidateSummariesByTimeRange?.totalCount,
            },
          };
        } else {
          const typedPrev = prev as GetInfringementsQuery;
          const typedFetchMoreResult = fetchMoreResult as GetInfringementsQuery;

          return {
            ...typedPrev,
            infringementSummariesByTimeRange: {
              ...typedPrev.infringementSummariesByTimeRange,
              items: [
                ...(typedPrev.infringementSummariesByTimeRange?.items || []),
                ...(typedFetchMoreResult.infringementSummariesByTimeRange?.items || []),
              ],
              pageInfo:
                typedFetchMoreResult.infringementSummariesByTimeRange?.pageInfo ||
                typedPrev.infringementSummariesByTimeRange?.pageInfo,
              totalCount:
                typedFetchMoreResult.infringementSummariesByTimeRange?.totalCount ||
                typedPrev.infringementSummariesByTimeRange?.totalCount,
            },
          };
        }
      },
    });
  };

  // Configuration
  const configSelectors = useConfigSelectors();
  const {
    globals: { '12HourFormat': twelveHourFormat },
    grid: { resultSize, loadMoreSize },
    infringements: {
      grid: { hiddenColumns },
    },
  } = configSelectors.getConfig();
  // Translation
  const languageSelectors = useLanguageSelectors();
  const {
    components: {
      buttons: { loadMore: loadMoreText },
      labels: {
        table: { endTable },
      },
      hints: { tryChangingFiltersOrDate },
      lists: { aircraftCategories, operationTypes, ruleTypes, severities, infringementStatuses },
    },
    screens: {
      infringements: {
        errors: { noDataFound },
      },
    },
  } = languageSelectors.getLanguage();

  const [translationDataList, setTranslationDataList] = useState<object[]>([]);
  useEffect(() => {
    setTranslationDataList(
      capitalizeObjectKeys({
        ...aircraftCategories,
        ...operationTypes,
        ...ruleTypes,
        ...severities,
        ...infringementStatuses,
      })
    );
  }, [aircraftCategories, operationTypes, ruleTypes, severities, infringementStatuses]);
  const columns = [
    'displayRuleName',
    'displayTime',
    'displayInfringement',
    'displaySeverity',
    'acid',
    'displayCategory',
    'aircraftType',
    'displayVectored',
  ];

  const loading = isLoading || isLoadingMore;
  const hiddenColumnsNonNull = hiddenColumns ? hiddenColumns : [];
  const showSeverity = !hiddenColumnsNonNull.includes('displaySeverity');
  // column types are used to add class-names to each column (used only for styling)
  // TODO: this logic needs to be handled in the design system, not here. We'll refactor when the table needs to be modified.
  const columnTypes = {
    rule: {
      title: 'rule',
      abbreviation: '',
    },
    acid: {
      title: 'record-title',
      abbreviation: '',
    },
    displayRunwayName: {
      title: 'runway',
      abbreviation: 'Rwy',
    },
    displayCategory: {
      title: 'aircraft',
      abbreviation: 'Categ',
    },
    displayTime: {
      title: 'time',
      abbreviation: '',
    },
    displayVectored: {
      title: 'vectored',
      abbreviation: '',
    },
    displayInfringement: {
      title: 'infringement',
      abbreviation: '',
      grow: showSeverity ? null : '2',
    },
    displaySeverity: {
      title: 'severity',
      abbreviation: '',
      grow: '2',
    },
    aircraftType: {
      title: 'model',
      abbreviation: '',
      grow: '3',
    },
  };

  const onSelectRow = id => {
    selectRow(id, dispatcher);
  };

  hideInfringementColumns({
    showSeverity,
    showCandidates: true,
    showStatus: true,
  });

  const handleVectoredChange = (bool, operationId) => {
    if (bool === true) {
      addInfringementVectored({
        variables: { ids: [operationId] },
      });
    } else {
      removeInfringementVectored({
        variables: { ids: [operationId] },
      });
    }
  };

  const [displayData, setDisplayData] = useState<IInfringementsData[]>([]);
  const { itemsIds, hasNextPage, endCursor, dateRange } = dataSelectors.getNavigationData();
  const ruleInfo = { itemsIds, hasNextPage, endCursor, dateRange };
  useEffect(() => {
    setDisplayData(
      formatATCInfringementData(
        tableLoading && !tableLoadingMore ? [] : data?.items || [],
        translationDataList,
        ruleInfo,
        handleVectoredChange,
        twelveHourFormat
      )
    );
  }, [latestData, translationDataList, tableLoading, tableLoadingMore]);

  // Connect map load state to table load state
  const { screenLoadStates, setScreenLoadStates } = useScreenLoadStateContext();
  const { mapLoadState, tableLoadState } = screenLoadStates;

  useEffect(() => {
    setScreenLoadStates({ type: 'SET_TABLE_LOAD_STATE', payload: tableLoading });
  }, [tableLoading]);

  // Custom override to get sort icons working in header component with new state
  const customSortSelectors = {
    getSortString: () => {
      const sortOb = state.variables.sort;
      return JSON.stringify(sortOb, null, 2);
    },
    getIconProps: (sortName: string) => {
      const { sort } = state?.variables;

      if (sortName === 'time' && !Array.isArray(sort)) {
        // do not show icon for time when it is implicit
        return false;
      }
      const sortArr = (sort as OrderByType[]) || [];
      if (sortArr.length > 1) {
        return false;
      }
      return sortArr.find(sortString => sortString.field && sortString.field === sortName) || false;
    },
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Handle isVectored parameter
    const isVectored = Boolean(urlParams.get('isVectored')) || undefined;
    if (isVectored !== undefined) {
      infringementFilterDispatch({
        type: InfringementTableDataActionType.UPDATE_TAG_FILTER,
        payload: {
          filter: isVectored ? 'includeOperationTags' : 'excludeOperationTags',
          value: ['Vector'],
        },
      });
    }
  }, []);

  useEffect(() => {
    // Update URL when candidates or infringement filter changes
    const updateURL = () => {
      const url = new URL(window.location.href);
      if (state.variables.filter.includeOperationTags?.some(tag => tag.name === 'Vector')) {
        url.searchParams.set('isVectored', 'true');
      } else if (state.variables.filter.excludeOperationTags?.some(tag => tag.name === 'Vector')) {
        url.searchParams.set('isVectored', 'false');
      } else {
        url.searchParams.delete('isVectored');
      }

      window.history.replaceState({}, '', url.toString());
    };

    updateURL();
  }, [state.variables.filter.excludeOperationTags, state.variables.filter.includeOperationTags]);

  return (
    <>
      <Table
        className={`infringements-table infringements-table--atc`}
        loading={tableLoadState || mapLoadState || isLoadingMore}
        rowHeaders={formatATCHeaders(
          resultSize,
          loading,
          dispatcher,
          customSortSelectors,
          languageSelectors.getLanguage()
        )}
        data={displayData}
        columns={columns}
        columnTypes={columnTypes}
        selectedData={selectedData}
        areAllRowsSelected={areAllRowsSelected}
        gridID="infringements"
        onSelectRow={onSelectRow}
        hasEnded={displayData.length && pageInfo && !pageInfo.hasNextPage}
        languageData={{ noDataTitle: noDataFound, noDataText: tryChangingFiltersOrDate, endTable }}
      />
      {!!data?.pageInfo?.hasNextPage && (
        <LoadMoreBar isLoading={tableLoadingMore} onClick={handleGetMore} disabled={tableLoading}>
          {loadMoreText}
        </LoadMoreBar>
      )}
    </>
  );
};
